import { useParams, useNavigate } from "react-router-dom";
import { useMemo, useState } from "react";
import api from "../../api/axios";

// Best-effort decode of a JWT-style invite token to surface the email
// in the disabled field. The token is base64url-encoded; payload may
// include "email". Returns null if anything goes sideways (signed
// opaque token, malformed, no email claim) — the UI just falls back to
// "your invited email" then. Never throws into the render.
function decodeInviteEmail(token) {
  if (!token || typeof token !== "string") return null;
  try {
    const parts = token.split(".");
    if (parts.length < 2) return null;
    let payload = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    while (payload.length % 4) payload += "=";
    const json = JSON.parse(atob(payload));
    return json?.email || json?.invited_email || null;
  } catch {
    return null;
  }
}

function maskEmail(email) {
  if (!email || typeof email !== "string" || !email.includes("@")) return email;
  const [local, domain] = email.split("@");
  if (local.length <= 2) return `${local[0] || ""}***@${domain}`;
  return `${local.slice(0, 2)}${"*".repeat(Math.max(1, local.length - 3))}${local.slice(-1)}@${domain}`;
}

export default function AcceptInvite() {
  const { token } = useParams();
  const navigate = useNavigate();
  const inviteEmail = useMemo(() => decodeInviteEmail(token), [token]);

  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    password: "",
    confirm_password: "",
  });

  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    // Client-side validation
    if (
      !form.first_name ||
      !form.last_name ||
      !form.password ||
      !form.confirm_password
    ) {
      setError("Please fill in all fields");
      setLoading(false);
      return;
    }

    if (form.password !== form.confirm_password) {
      setError("Passwords do not match");
      setLoading(false);
      return;
    }

    api
      .post("/api/organizations/invite/accept/", {
        token,
        ...form,
      })
      .then((res) => {
        // Use the org name when available — "Welcome to <Acme>!" reads
        // friendlier than the generic "Successfully joined".
        const orgName = res.data?.organization_name
          || res.data?.organization?.name
          || null;
        const msg = orgName
          ? `Welcome to ${orgName}!`
          : (res.data?.message || "Successfully joined the organization!");
        setSuccess(msg);
        setTimeout(() => {
          navigate("/login");
        }, 2500);
      })
      .catch((err) => {
        setError(
          err.response?.data?.detail ||
            err.response?.data?.message ||
            "Invite acceptance failed. Please try again.",
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // Success State
  if (success) {
    return (
      <div className="bg-bg-primary min-h-screen text-text-primary flex justify-center items-center p-4">
        <div className="bg-bg-secondary rounded-2xl p-12 max-w-md w-full text-center border border-bg-main shadow-2xl">
          <div className="w-20 h-20 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg
              className="w-10 h-10 text-emerald-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-text-primary mb-3">
            {success}
          </h2>
          <p className="text-text-secondary mb-6">
            Redirecting you to login...
          </p>
          <div className="w-16 h-16 border-4 border-button-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
        </div>
      </div>
    );
  }

  // Main Form
  return (
    <div className="bg-bg-primary min-h-screen text-text-primary flex justify-center items-center p-4">
      <div className="bg-bg-secondary rounded-2xl p-8 md:p-12 max-w-2xl w-full border border-bg-main shadow-2xl">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-br from-purple-500/20 to-blue-500/20 rounded-xl flex items-center justify-center mx-auto mb-4 border border-purple-500/30">
            <svg
              className="w-8 h-8 text-purple-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              />
            </svg>
          </div>
          <h1 className="text-3xl font-bold text-text-primary mb-3">
            Organization Invitation
          </h1>
          <p className="text-text-secondary">
            You've been invited to join an organization. Complete your profile
            to get started.
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Name Fields */}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-text-secondary mb-2">
                First Name <span className="text-red-400">*</span>
              </label>
              <input
                name="first_name"
                value={form.first_name}
                onChange={handleChange}
                className="w-full p-3 px-4 rounded-lg bg-bg-main text-text-primary border border-bg-secondary focus:border-button-primary outline-none transition-colors"
                placeholder="John"
                required
              />
            </div>
            <div>
              <label className="block text-sm text-text-secondary mb-2">
                Last Name <span className="text-red-400">*</span>
              </label>
              <input
                name="last_name"
                value={form.last_name}
                onChange={handleChange}
                className="w-full p-3 px-4 rounded-lg bg-bg-main text-text-primary border border-bg-secondary focus:border-button-primary outline-none transition-colors"
                placeholder="Doe"
                required
              />
            </div>
          </div>

          {/* Email Field (Disabled) */}
          <div>
            <label className="block text-sm text-text-secondary mb-2">
              Email Address
            </label>
            <div className="relative">
              <input
                value={inviteEmail ? maskEmail(inviteEmail) : "Your invited email address"}
                disabled
                className="w-full p-3 px-4 rounded-lg bg-bg-main text-text-secondary border border-bg-secondary outline-none opacity-80 cursor-not-allowed font-mono text-sm"
              />
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                <svg
                  className="w-5 h-5 text-text-secondary"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                  />
                </svg>
              </div>
            </div>
            <p className="text-xs text-text-secondary mt-1">
              This email is pre-assigned from your invitation
            </p>
          </div>

          {/* Password Fields */}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-text-secondary mb-2">
                Password <span className="text-red-400">*</span>
              </label>
              <input
                type="password"
                name="password"
                value={form.password}
                onChange={handleChange}
                className="w-full p-3 px-4 rounded-lg bg-bg-main text-text-primary border border-bg-secondary focus:border-button-primary outline-none transition-colors"
                placeholder="••••••••"
                required
              />
            </div>
            <div>
              <label className="block text-sm text-text-secondary mb-2">
                Confirm Password <span className="text-red-400">*</span>
              </label>
              <input
                type="password"
                name="confirm_password"
                value={form.confirm_password}
                onChange={handleChange}
                className="w-full p-3 px-4 rounded-lg bg-bg-main text-text-primary border border-bg-secondary focus:border-button-primary outline-none transition-colors"
                placeholder="••••••••"
                required
              />
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg flex items-start gap-3">
              <svg
                className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 px-6 rounded-lg font-medium transition-all duration-200 shadow-lg flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ backgroundColor: "#6B1991", color: "#FFFFFF" }}
          >
            {loading ? (
              <>
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Joining...
              </>
            ) : (
              <>
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"
                  />
                </svg>
                Join Organization
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 7l5 5m0 0l-5 5m5-5H6"
                  />
                </svg>
              </>
            )}
          </button>
        </form>

        {/* Info Box */}
        <div className="mt-6 p-4 bg-bg-main rounded-lg border border-bg-secondary">
          <div className="flex items-start gap-3">
            <svg
              className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <div>
              <p className="text-text-primary text-sm font-medium mb-1">
                What happens next?
              </p>
              <p className="text-text-secondary text-xs">
                After joining, you'll be redirected to login with your new
                credentials. You'll have access to all organization resources
                based on your assigned role.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
